import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import type { JobOpening } from "@shared/schema";

const jobFormSchema = z.object({
  title: z.string().min(2, "Title is required"),
  description: z.string().min(10, "Description is required"),
  location: z.string().min(1, "Location is required"),
  type: z.string().min(1, "Type is required"),
  experience: z.string().min(1, "Experience is required"),
  skills: z.string().min(1, "At least one skill is required"),
  isNew: z.boolean().optional(),
});

type JobFormValues = z.infer<typeof jobFormSchema>;

function getAuthHeader() {
  const token = localStorage.getItem("adminToken");
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

function safeParseSkills(skills: unknown): string[] {
  try {
    if (Array.isArray(skills)) return skills.filter((x): x is string => typeof x === "string");
    if (typeof skills === "string") {
      const parsed: unknown = JSON.parse(skills);
      return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
    }
    return [];
  } catch {
    return [];
  }
}

export default function JobsManager() {
  const [showForm, setShowForm] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: jobs = [], isLoading } = useQuery<JobOpening[]>({
    queryKey: ["/api/jobs"],
  });

  const form = useForm<JobFormValues>({
    resolver: zodResolver(jobFormSchema),
    defaultValues: {
      title: "",
      description: "",
      location: "Jaipur",
      type: "Full-time",
      experience: "2+ years",
      skills: "",
      isNew: true,
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: JobFormValues) => {
      // Convert comma-separated skills to JSON array string
      const skillsArray = data.skills
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const body = {
        ...data,
        skills: JSON.stringify(skillsArray),
        isNew: data.isNew ?? true,
      };
      const res = await fetch("/api/admin/jobs", {
        method: "POST",
        headers: getAuthHeader(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Job posted ✅", description: "The job opening is now live on the Careers page." });
      form.reset();
      setShowForm(false);
      queryClient.invalidateQueries({ queryKey: ["/api/jobs"] });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create job.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/admin/jobs/${id}`, {
        method: "DELETE",
        headers: getAuthHeader(),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Deleted", description: "Job opening removed successfully." });
      queryClient.invalidateQueries({ queryKey: ["/api/jobs"] });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete job.", variant: "destructive" });
    },
  });

  const onSubmit = (data: JobFormValues) => createMutation.mutate(data);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Job Openings</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {jobs.length} active opening{jobs.length !== 1 ? "s" : ""} on the Careers page
          </p>
        </div>
        <Button
          onClick={() => setShowForm((v) => !v)}
          className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-blue-600 dark:hover:bg-blue-700"
        >
          <i className="fas fa-plus mr-2" />
          {showForm ? "Cancel" : "Post New Job"}
        </Button>
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-6">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">
            <i className="fas fa-briefcase mr-2 text-blue-500" />
            New Job Opening
          </h3>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="dark:text-white">Job Title *</Label>
                <Input
                  className="mt-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                  placeholder="e.g. Sales Executive"
                  {...form.register("title")}
                />
                {form.formState.errors.title && (
                  <p className="text-red-500 text-xs mt-1">{form.formState.errors.title.message}</p>
                )}
              </div>
              <div>
                <Label className="dark:text-white">Location *</Label>
                <Input
                  className="mt-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                  placeholder="e.g. Jaipur / Remote"
                  {...form.register("location")}
                />
              </div>
              <div>
                <Label className="dark:text-white">Type *</Label>
                <Input
                  className="mt-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                  placeholder="Full-time / Part-time / Contract"
                  {...form.register("type")}
                />
              </div>
              <div>
                <Label className="dark:text-white">Experience *</Label>
                <Input
                  className="mt-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                  placeholder="e.g. 2+ years"
                  {...form.register("experience")}
                />
              </div>
            </div>

            <div>
              <Label className="dark:text-white">Description *</Label>
              <Textarea
                className="mt-1 min-h-[120px] dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                placeholder="Describe the role, responsibilities, and what you're looking for..."
                {...form.register("description")}
              />
              {form.formState.errors.description && (
                <p className="text-red-500 text-xs mt-1">{form.formState.errors.description.message}</p>
              )}
            </div>

            <div>
              <Label className="dark:text-white">Skills (comma-separated) *</Label>
              <Input
                className="mt-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                placeholder="e.g. React, TypeScript, Node.js"
                {...form.register("skills")}
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Separate each skill with a comma
              </p>
              {form.formState.errors.skills && (
                <p className="text-red-500 text-xs mt-1">{form.formState.errors.skills.message}</p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isNew"
                className="h-4 w-4 rounded"
                {...form.register("isNew")}
                defaultChecked
              />
              <Label htmlFor="isNew" className="cursor-pointer dark:text-white">
                Mark as "New" (highlights the job on the Careers page)
              </Label>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                disabled={createMutation.isPending}
                className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-blue-600 dark:hover:bg-blue-700"
              >
                {createMutation.isPending ? "Posting..." : "Post Job"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Jobs List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-28 rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-10 text-center">
          <i className="fas fa-briefcase text-3xl text-slate-300 dark:text-slate-600 mb-3 block" />
          <p className="text-slate-500 dark:text-slate-400 font-medium">No job openings yet.</p>
          <p className="text-slate-400 dark:text-slate-500 text-sm mt-1">Click "Post New Job" to add your first one.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <div
              key={job.id}
              className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 flex flex-col sm:flex-row sm:items-start gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h4 className="font-bold text-slate-900 dark:text-white">{job.title}</h4>
                  {job.isNew && (
                    <span className="rounded-full bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs font-bold px-2 py-0.5">
                      NEW
                    </span>
                  )}
                  <span className="text-xs text-slate-400 dark:text-slate-500">ID: {job.id}</span>
                </div>
                <div className="flex flex-wrap gap-2 text-xs text-slate-500 dark:text-slate-400 mb-2">
                  <span><i className="fas fa-map-marker-alt mr-1" />{job.location}</span>
                  <span><i className="fas fa-clock mr-1" />{job.type}</span>
                  <span><i className="fas fa-star mr-1" />{job.experience}</span>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2">{job.description}</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {safeParseSkills(job.skills).slice(0, 6).map((skill, i) => (
                    <span
                      key={i}
                      className="rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs px-2 py-0.5"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex sm:flex-col gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-500 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20"
                  disabled={deleteMutation.isPending}
                  onClick={() => {
                    if (confirm(`Delete "${job.title}"?`)) deleteMutation.mutate(job.id);
                  }}
                >
                  <i className="fas fa-trash mr-1" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
